import { useAuth } from 'react-oidc-context'

/**
 * Parses an ABAC claim value that may arrive as either a JSON-encoded string
 * (Cognito custom attributes: "[\"politics\",\"cyber\"]") or a plain array
 * (providers that decode claims automatically).
 */
function parseClaim(value: unknown): string[] {
  if (!value) return []
  if (Array.isArray(value)) return value as string[]
  if (typeof value === 'string') {
    try { return JSON.parse(value) as string[] } catch { return [] }
  }
  return []
}

/**
 * Typed profile from the Cognito ID token claims (AuthenticatedClient schema).
 * All fields except `sub` are optional — the token may not include them
 * depending on the Cognito user pool configuration.
 */
export type WireUser = {
  sub: string
  /** LDAP username (from `cognito:username` claim, e.g. "qc_quinn"). Use this to compare against *AssignedTo fields. */
  username?: string
  name?: string
  given_name?: string
  family_name?: string
  email?: string
  /** LDAP distinguished name */
  dn?: string
  /** Agency code e.g. "PRIME" */
  agency?: string
  /** Primary org path e.g. "abc/123" */
  primary_organization?: string
  /** Assigned org path e.g. "efg/456/foo" */
  assigned_organization?: string
  /** Entitlement strings */
  entitlements?: string[]
  /** Workflow role group names from IDAM (RBAC) */
  uiasgroups: string[]
  /** Content topic slugs this user may access (ABAC) */
  topics: string[]
  /** Geographic region slugs this user may access (ABAC) */
  regions: string[]
}

/**
 * Returns the authenticated user's ID token and typed profile.
 * Must be used within a component tree wrapped by AuthGuard.
 */
export function useWireAuth() {
  const auth = useAuth()

  // Normalize the OIDC profile onto WireUser, defaulting array fields to []
  // so consumers never have to guard against undefined on required arrays.
  const rawProfile = auth.user?.profile as Record<string, unknown> | undefined
  const topicsClaim  = import.meta.env.VITE_TOPICS_CLAIM  ?? 'custom:topics'
  const regionsClaim = import.meta.env.VITE_REGIONS_CLAIM ?? 'custom:regions'
  const profile: WireUser | undefined = rawProfile
    ? {
        ...(rawProfile as WireUser),
        username:   (rawProfile['cognito:username'] as string | undefined),
        uiasgroups: (rawProfile[import.meta.env.VITE_GROUP_JWT_CLAIM ?? 'cognito:groups'] as string[] | undefined)
                 ?? (rawProfile[import.meta.env.VITE_GROUP_RESPONSE_KEY ?? 'uiasgroups'] as string[] | undefined)
                 ?? [],
        topics:     parseClaim(rawProfile[topicsClaim]),
        regions:    parseClaim(rawProfile[regionsClaim]),
      }
    : undefined
  const idToken = auth.user?.id_token

  return {
    user: profile,
    idToken,
    /** Bearer value ready to use as Authorization header */
    bearerToken: idToken ? `Bearer ${idToken}` : undefined,
    isAuthenticated: auth.isAuthenticated,
    logout: () => {
      // Clear the local token first so the app doesn't consider the user
      // authenticated when Cognito redirects back after clearing its cookie.
      const storageKey = `oidc.user:${import.meta.env.VITE_OIDC_AUTHORITY}:${import.meta.env.VITE_OIDC_CLIENT_ID}`
      sessionStorage.removeItem(storageKey)
      const domain = import.meta.env.VITE_COGNITO_DOMAIN as string
      const clientId = import.meta.env.VITE_OIDC_CLIENT_ID as string
      const logoutUri = encodeURIComponent(`${window.location.origin}/`)
      window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`
    },
  }
}
